import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'

import Heart from '../../../../quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'
import { IconButton } from '../icon-button'
import { Link } from '../link'
import ProductInfoPrice from '../productInfoPrice/ProductInfoPrice'
import { ProductTraditionalDiscountMessage } from '../productTraditionalDiscountMessage'
import { PurchaseType } from '../purchaseTypeSelector/PurchaseTypeSelector'

import styles from './productInfo.module.scss'
import ProductInfoReviewSummary from './ProductInfoReviewSummary'

export interface ProductInfoProps {
  product?: Product
  productInfoHeaderClassName?: string
  productInfoHeaderTextClassName?: string
  productTitle: string
  formattedSalePrice?: string
  hideSalePrice?: boolean
  formattedTraditionalPrice?: string
  isWishListed?: boolean
  className?: string
  avgRating?: number
  reviewCount?: number
  onWishListToggle?: (e: React.MouseEvent<HTMLElement>) => void
  hideWishListIcon?: boolean
  hideReviewSummary?: boolean
  onReviewSummaryClick?: (e: React.MouseEvent<HTMLElement>) => void
  savingPercentage?: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  formattedFestivalOfferPrice?: string
  showFullPDP?: boolean
  hideProductDetail?: boolean
  hideReviewCount?: boolean
  hideReviewLabel?: boolean
  productInfoReviewClassName?: string
  inStockMsgClass?: string
  showDiscount?: boolean
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  pricePerServing?: number
  traditionalPricePerServing?: number
  hideSavings?: boolean
  showNewPdpDesignV2?: boolean
  isBundle?: boolean
  isFinalSaleProduct?: boolean
  isB2BFinalSaleProduct?: boolean
  formattedSubscriptionPrice?: string
  purchaseOption: PurchaseType
  updatedDesignForV2?: boolean
  containerRef?: React.RefObject<HTMLDivElement>
  isVariantThirdParty?: boolean
  selectedOptions?: ProductVariantOption[]
}

const ProductInfo = ({
  product,
  productInfoHeaderClassName,
  productInfoHeaderTextClassName,
  productTitle,
  formattedSalePrice,
  hideSalePrice = false,
  formattedTraditionalPrice,
  isWishListed = false,
  className,
  avgRating,
  reviewCount,
  onWishListToggle,
  hideWishListIcon,
  hideReviewSummary,
  onReviewSummaryClick,
  savingPercentage,
  brandName,
  brandLink,
  traditionalDiscountMessage,
  formattedFestivalOfferPrice,
  hideReviewCount,
  hideReviewLabel,
  productInfoReviewClassName,
  showDiscount,
  festivalPricePerUnit,
  subscriptionOfferPricePerUnit,
  pricePerServing,
  traditionalPricePerServing,
  hideSavings,
  showNewPdpDesignV2,
  isBundle,
  isFinalSaleProduct,
  isB2BFinalSaleProduct,
  formattedSubscriptionPrice,
  purchaseOption,
  updatedDesignForV2 = false,
  containerRef,
  isVariantThirdParty,
  selectedOptions,
}: ProductInfoProps) => {
  const { t } = useTranslation('common')
  const classes = classnames(styles.container, className)
  const STYLE_BLACK = 'black'
  const STYLE_WHITE = 'white'
  const STYLE_WIDTH = '20px'
  const STYLE_HEIGHT = '18px'
  const innerStyle = {
    // eslint-disable-next-line i18next/no-literal-string
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
    // eslint-disable-next-line i18next/no-literal-string
    width: STYLE_WIDTH,
    // eslint-disable-next-line i18next/no-literal-string
    height: STYLE_HEIGHT,
  }
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const reviewSummaryNotVisible = hideReviewSummary || !avgRating || Number(reviewCount) <= 0
  return (
    <div
      ref={containerRef}
      data-testid="product-info"
      data-show-new-pdp-design-v2={showNewPdpDesignV2?.toString()}
      data-is-bundle={isBundle?.toString()}
      className={classnames(
        classes,
        styles.container_adjustedPadding,
        // showNewPdpDesignV2 && styles.container_newDesignV2,
        updatedDesignForV2 && styles.container_updatedDesignV2,
        isVariantThirdParty && styles.container_thirdParty,
        isBundle && styles.container_newDesignV2_bundle,
      )}
    >
      {brandName &&
        !isVariantThirdParty &&
        (brandLink ? (
          <Link
            href={brandLink}
            className={classnames(
              styles.container__brandName,
              showNewPdpDesignV2 && styles.container__brandName_newDesignV2,
            )}
          >
            {brandName}
          </Link>
        ) : (
          <div
            className={classnames(
              styles.container__brandNameWithoutLink,
              showNewPdpDesignV2 && styles.container__brandNameWithoutLink_newDesignV2,
            )}
          >
            {brandName}
          </div>
        ))}
      <div
        className={classnames(
          styles.container__header,
          productInfoHeaderClassName,
          styles.container__header_adjustedPadding,
          showNewPdpDesignV2 && styles.container__header_newDesignV2,
          updatedDesignForV2 && styles.container__header_updatedDesignV2,
        )}
      >
        <h1
          className={classnames(
            styles.container__header__text,
            productInfoHeaderTextClassName,
            styles.container__header__text_adjustedPadding,
            showNewPdpDesignV2 && styles.container__header__text_newDesignV2,
            reviewSummaryNotVisible && styles.container__header__text_newDesignV2_reviewSummaryNotVisible,
          )}
        >
          {productTitle}
        </h1>
        {!hideWishListIcon && (
          <IconButton
            className={classnames(styles.container__header__icon, styles.container__header__icon_adjustedPadding)}
            iconSvg={Heart}
            innerStyle={innerStyle}
            onClick={onWishListToggle}
            ariaLabel={t('mark_product_as_favourite')}
            dataTestId={isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON}
          />
        )}
        {showNewPdpDesignV2 ? (
          <ProductInfoReviewSummary
            avgRating={avgRating}
            reviewCount={reviewCount}
            onReviewSummaryClick={onReviewSummaryClick}
            hideReviewCount={hideReviewCount}
            hideReviewLabel={true}
            productInfoReviewClassName={productInfoReviewClassName}
            hideReviewSummary={hideReviewSummary}
            showNewPdpDesignV2={showNewPdpDesignV2}
            updatedDesignForV2={updatedDesignForV2}
          />
        ) : null}
      </div>
      {!showNewPdpDesignV2 && (
        <ProductInfoReviewSummary
          avgRating={avgRating}
          reviewCount={reviewCount}
          onReviewSummaryClick={onReviewSummaryClick}
          hideReviewCount={hideReviewCount}
          hideReviewLabel={hideReviewLabel}
          productInfoReviewClassName={productInfoReviewClassName}
          hideReviewSummary={hideReviewSummary}
          updatedDesignForV2={updatedDesignForV2}
        />
      )}
      {!hideSalePrice && !!formattedSalePrice && (
        <ProductInfoPrice
          product={product}
          formattedSubscriptionPrice={formattedSubscriptionPrice}
          formattedSalePrice={formattedSalePrice}
          formattedTraditionalPrice={formattedTraditionalPrice}
          savingPercentage={savingPercentage}
          formattedFestivalOfferPrice={formattedFestivalOfferPrice}
          showDiscount={showDiscount}
          subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
          pricePerServing={pricePerServing}
          traditionalPricePerServing={traditionalPricePerServing}
          festivalPricePerUnit={festivalPricePerUnit}
          hideSavings={hideSavings}
          showNewPdpDesignV2={showNewPdpDesignV2}
          isFinalSaleProduct={isFinalSaleProduct}
          isB2BFinalSaleProduct={isB2BFinalSaleProduct}
          purchaseOption={purchaseOption}
          updatedDesignForV2={updatedDesignForV2}
          isVariantThirdParty={isVariantThirdParty}
          selectedOptions={selectedOptions}
        />
      )}

      <ProductTraditionalDiscountMessage
        formattedTraditionalPrice={formattedTraditionalPrice}
        traditionalDiscountMessage={traditionalDiscountMessage}
      />
    </div>
  )
}

export default ProductInfo
